import { useEffect, useState } from 'react'
import BuyCreditsModal from '../components/buyCreditsModal'
import ConfirmModal from '../components/confirmModal'
import DifficultyBadge from '../components/difficultyBadge'
import { EXAM_STAGES } from '../constants/config'
import { STRINGS } from '../constants/strings'
import { useAuth, useExam } from '../hooks'
import { scoreObjective, scoreMsqCredit } from '../utils/grading'
import { api } from '../data/api'


const SA_VERDICT = {
  correct:   { label: 'Correct',          wrapper: 'bg-emerald-500/[0.08] border-emerald-500/20', text: 'text-emerald-400', icon: '✓' },
  partial:   { label: 'Partially Correct', wrapper: 'bg-amber-500/[0.08]   border-amber-500/20',   text: 'text-amber-400',   icon: '~' },
  incorrect: { label: 'Incorrect',         wrapper: 'bg-red-500/[0.08]     border-red-500/20',     text: 'text-red-400',     icon: '✗' },
}

function FeedbackPanel({ q, userAnswer, saResult }) {
  if (q.type === 'mcq' || q.type === 'true_false' || q.type === 'msq') {
    const isMsq  = q.type === 'msq'
    const credit = isMsq ? scoreMsqCredit(userAnswer, q.correct_answers, Object.keys(q.options ?? {}).length) : (scoreObjective(q, userAnswer) ? 1 : 0)
    const correct = credit === 1
    const partial = isMsq && credit > 0 && credit < 1
    const cfg = correct
      ? { wrapper: 'bg-emerald-500/[0.08] border-emerald-500/20', text: 'text-emerald-400', label: STRINGS.feedback.correct }
      : partial
      ? { wrapper: 'bg-amber-500/[0.08] border-amber-500/20',     text: 'text-amber-400',   label: '~ Partially Correct' }
      : { wrapper: 'bg-red-500/[0.08] border-red-500/20',         text: 'text-red-400',     label: STRINGS.feedback.incorrect }
    const correctDisplay = q.type === 'mcq'
      ? `${q.correct_answer}) ${q.options[q.correct_answer]}`
      : q.type === 'msq'
      ? (q.correct_answers ?? []).map(k => `${k}) ${q.options[k]}`).join(', ')
      : q.correct_answer

    return (
      <div className={`rounded-2xl p-4 border animate-fade-up ${cfg.wrapper}`}>
        <div className={`font-semibold text-sm mb-2 ${cfg.text}`}>
          <div className="whitespace-nowrap">{cfg.label}</div>
          {!correct && (
            <div className="text-[var(--text-3)] font-normal mt-1">
              {STRINGS.feedback.correctAnswer} <span className="text-emerald-400 font-medium">{correctDisplay}</span>
            </div>
          )}
        </div>
        {q.explanation && <p className="text-[var(--text-2)] text-xs leading-relaxed">{q.explanation}</p>}
      </div>
    )
  }

  // Short answer — AI graded result
  if (!saResult) return null
  const cfg = SA_VERDICT[saResult.verdict] ?? SA_VERDICT.incorrect

  return (
    <div className={`rounded-2xl p-4 border animate-fade-up ${cfg.wrapper}`}>
      <p className={`font-semibold text-sm mb-3 ${cfg.text}`}>{cfg.icon} {cfg.label}</p>

      {/* Key points breakdown */}
      {(saResult.keyPointsCovered?.length > 0 || saResult.keyPointsMissed?.length > 0) && (
        <div className="mb-3 space-y-1.5">
          {saResult.keyPointsCovered?.map((kp, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className="text-emerald-400 flex-shrink-0 mt-0.5 font-bold">✓</span>
              <span className="text-[var(--text-2)]">{kp}</span>
            </div>
          ))}
          {saResult.keyPointsMissed?.map((kp, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className="text-red-400 flex-shrink-0 mt-0.5 font-bold">✗</span>
              <span className="text-[var(--text-3)]">{kp}</span>
            </div>
          ))}
        </div>
      )}

      {/* Feedback sentence */}
      {saResult.feedback && (
        <p className="text-xs text-[var(--text-2)] mb-3">{saResult.feedback}</p>
      )}

      {/* Model answer */}
      <p className="text-[var(--text-3)] text-xs font-medium mb-1">{STRINGS.feedback.modelAnswer}</p>
      <p className="text-[var(--text-2)] text-xs leading-relaxed">{q.correct_answer}</p>
    </div>
  )
}

function QuestionCard({ q, idx }) {
  const { state, dispatch } = useExam()
  const { user, updateUser } = useAuth()
  const shown    = state.feedbackShown[idx] ?? false
  const answer   = state.answers[idx] ?? (q.type === 'msq' ? [] : '')
  const hasAns   = Array.isArray(answer) ? answer.length > 0 : answer.trim().length > 0
  const total    = state.questions.length
  const saResult = state.shortAnswerResults[idx] ?? null
  // Same "has its own billable balance" test as profilePage's hasOwnBalance —
  // an org admin previewing their own exam bills to their own org's balance
  // and can buy credits directly, same as a standalone user. Only an
  // org-linked examinee (role 'user' WITH an orgId) has no balance of their
  // own and must be told to contact their organisation instead.
  const canBuyCreditsDirectly = user?.role === 'organisation' || (user?.role === 'user' && !user?.orgId)

  const [checking,   setChecking]   = useState(false)
  const [checkError, setCheckError] = useState(null)
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)

  const set = v => dispatch({ type: 'SET_ANSWER', payload: { idx, answer: v } })
  const toggleMsq = key => {
    const cur  = Array.isArray(answer) ? answer : []
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]
    set(next)
  }

  async function handleCheck() {
    if (q.type !== 'short_answer') {
      dispatch({ type: 'SHOW_FEEDBACK', payload: idx })
      return
    }
    setChecking(true)
    setCheckError(null)
    try {
      const result = await api.post('/api/ai/check-short-answer', {
        question:      q.question,
        studentAnswer: answer,
        correctAnswer: q.correct_answer,
        keyPoints:     q.key_points ?? [],
        topic:         q.topic,
        examId:        state.selectedExam?.id ?? state.selectedExam?._id,
        marks:         q.marks,
      })
      dispatch({ type: 'SET_SHORT_ANSWER_RESULT', payload: { idx, result } })
      dispatch({ type: 'SHOW_FEEDBACK', payload: idx })
    } catch (err) {
      if (err.code === 'INSUFFICIENT_CREDITS' && canBuyCreditsDirectly) {
        // A standalone user or org admin pays for their own usage, so they
        // can act on this immediately — an org-linked examinee instead just
        // gets told to contact their organisation (see the message itself),
        // since only the org can buy more.
        setBuyCreditsOpen(true)
      } else {
        setCheckError(err.message || "Couldn't grade that right now — try again.")
      }
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-[var(--text-3)]">
        <span className="px-2.5 py-1 rounded-full border font-mono font-semibold text-[var(--text-2)] flex-shrink-0"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          {idx + 1} / {total}
        </span>
        {q.topic && <span className="truncate min-w-0 max-w-[200px]">· {q.topic}</span>}
        <DifficultyBadge level={state.difficulty} />
        {state.selectedExam?.showQuestionTypeIndicator && (q.type === 'mcq' || q.type === 'msq') && (
          <span className="px-2 py-0.5 rounded-full border font-semibold flex-shrink-0"
                style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--brand)' }}>
            {q.type === 'msq' ? 'Multiple options are selectable' : 'Only one option is selectable'}
          </span>
        )}
        <span className="ml-auto px-2 py-0.5 rounded-full border font-semibold flex-shrink-0"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-2)' }}>
          {q.marks ?? 1} {q.marks === 1 ? 'mark' : 'marks'}
        </span>
      </div>

      <h2 className="text-base sm:text-lg font-semibold text-[var(--text-1)] leading-relaxed mb-4 sm:mb-6">{q.question}</h2>

      {q.type === 'mcq' && (
        <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-6">
          {Object.entries(q.options).map(([key, val]) => {
            const selected  = answer === key
            const isCorrect = shown && key === q.correct_answer
            const isWrong   = shown && selected && !isCorrect
            return (
              <button key={key} disabled={shown} onClick={() => set(key)}
                className={`w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border text-left transition-all text-sm disabled:cursor-default ${
                  isCorrect  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                  : isWrong  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                  : selected ? 'bg-[var(--sel-bg)] border-[var(--sel-border)] text-[var(--sel-text)]'
                  : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-2)] hover:text-[var(--text-1)]'
                }`}
              >
                <span className={`w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0 flex items-center justify-center rounded-lg text-xs font-bold ${
                  isCorrect  ? 'bg-emerald-500/20 text-emerald-400'
                  : isWrong  ? 'bg-red-500/20 text-red-400'
                  : selected ? 'bg-[var(--sel-bg)] text-[var(--sel-text)]'
                  : 'bg-[var(--surface-2)] text-[var(--text-3)]'
                }`}>{key}</span>
                <span className="leading-snug">{val}</span>
                {isCorrect && <span className="ml-auto text-emerald-400">✓</span>}
                {isWrong   && <span className="ml-auto text-red-400">✗</span>}
              </button>
            )
          })}
        </div>
      )}

      {q.type === 'msq' && (
        <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-6">
          {Object.entries(q.options).map(([key, val]) => {
            const selected  = Array.isArray(answer) && answer.includes(key)
            const isCorrect = shown && (q.correct_answers ?? []).includes(key)
            const isWrong   = shown && selected && !isCorrect
            return (
              <button key={key} disabled={shown} onClick={() => toggleMsq(key)}
                className={`w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border text-left transition-all text-sm disabled:cursor-default ${
                  isCorrect  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                  : isWrong  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                  : selected ? 'bg-[var(--sel-bg)] border-[var(--sel-border)] text-[var(--sel-text)]'
                  : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-2)] hover:text-[var(--text-1)]'
                }`}
              >
                <span className={`w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0 flex items-center justify-center rounded-lg text-xs font-bold ${
                  isCorrect  ? 'bg-emerald-500/20 text-emerald-400'
                  : isWrong  ? 'bg-red-500/20 text-red-400'
                  : selected ? 'bg-[var(--sel-bg)] text-[var(--sel-text)]'
                  : 'bg-[var(--surface-2)] text-[var(--text-3)]'
                }`}>{key}</span>
                <span className="leading-snug">{val}</span>
                {isCorrect && <span className="ml-auto text-emerald-400">✓</span>}
                {isWrong   && <span className="ml-auto text-red-400">✗</span>}
              </button>
            )
          })}
        </div>
      )}

      {q.type === 'true_false' && (
        <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6">
          {['True', 'False'].map(opt => {
            const selected  = answer === opt
            const isCorrect = shown && opt === q.correct_answer
            const isWrong   = shown && selected && opt !== q.correct_answer
            return (
              <button key={opt} disabled={shown} onClick={() => set(opt)}
                className={`flex-1 py-2.5 sm:py-3 rounded-xl border font-semibold text-sm transition-all disabled:cursor-default ${
                  isCorrect  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : isWrong  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                  : selected ? 'bg-[var(--sel-bg)] border-[var(--sel-border)] text-[var(--sel-text)]'
                  : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-2)]'
                }`}
              >{opt}</button>
            )
          })}
        </div>
      )}

      {q.type === 'short_answer' && (
        <div className="mb-6">
          <textarea
            disabled={shown}
            value={answer}
            onChange={e => set(e.target.value.slice(0, 500))}
            placeholder="Write your answer here…"
            maxLength={500}
            className="w-full h-28 rounded-xl px-4 py-3 text-sm text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none resize-none disabled:opacity-60 disabled:cursor-default transition-colors"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
          />
          <p className={`text-right text-[0.65rem] mt-1 ${answer.length >= 500 ? 'text-red-400' : 'text-[var(--text-3)]'}`}>
            {answer.length}/500
          </p>
        </div>
      )}

      {!shown ? (
        <div>
          <button onClick={handleCheck} disabled={!hasAns || checking} className="btn-primary">
            {checking
              ? <><span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin inline-block mr-2" />Checking…</>
              : 'Check Answer'}
          </button>
          {checkError && (
            <p className="text-xs text-red-400 mt-2">{checkError}</p>
          )}
        </div>
      ) : (
        <FeedbackPanel q={q} userAnswer={answer} saResult={saResult} />
      )}

      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={msg => setCheckError(msg)} onClose={() => setBuyCreditsOpen(false)} />
      )}
    </div>
  )
}

export default function PracticeMode() {
  const { state, dispatch, reset } = useExam()
  const { questions, currentIdx } = state
  const total    = questions.length
  const idx      = Math.max(0, Math.min(currentIdx, total - 1))
  const answered    = Object.values(state.feedbackShown).filter(Boolean).length
  const hasAnyAnswer = Object.values(state.answers).some(a => String(a).trim().length > 0)
  const [key,        setKey]        = useState(0)
  const [showQuit,   setShowQuit]   = useState(false)

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowRight' && idx < total - 1) dispatch({ type: 'SET_CURRENT_IDX', payload: idx + 1 })
      if (e.key === 'ArrowLeft'  && idx > 0)         dispatch({ type: 'SET_CURRENT_IDX', payload: idx - 1 })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [idx, total])

  function go(n) {
    dispatch({ type: 'SET_CURRENT_IDX', payload: n })
    setKey(k => k + 1)
  }

  return (
    <>
    {showQuit && (
      <ConfirmModal
        danger
        title="Quit exam?"
        message="Your progress will be lost and you'll go back to the setup screen."
        confirmLabel="Quit"
        cancelLabel="Keep going"
        onConfirm={reset}
        onCancel={() => setShowQuit(false)}
      />
    )}
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-6 lg:py-8 flex gap-6 lg:gap-8 overflow-x-hidden">

      {/* ── Sidebar (desktop only) ───────────────────────────── */}
      <aside className="hidden lg:flex flex-col gap-4 w-56 flex-shrink-0 sticky top-20 self-start" style={{ maxHeight: 'calc(100vh - 6rem)', overflow: 'hidden' }}>
        <div className="card p-4 flex flex-col min-h-0 flex-1">
          <div className="flex items-center justify-between text-xs mb-3">
            <span style={{ color: 'var(--text-3)' }}>Progress</span>
            <span className="font-semibold" style={{ color: 'var(--brand)' }}>{answered}/{total}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden mb-4" style={{ background: 'var(--surface-2)' }}>
            <div className="h-full rounded-full transition-all duration-500"
                 style={{ width: `${(answered / total) * 100}%`, background: 'var(--brand)' }} />
          </div>
          <div className="grid grid-cols-5 gap-1 overflow-y-auto flex-1 content-start">
            {questions.map((_, i) => (
              <button key={i} onClick={() => go(i)}
                className="aspect-square rounded-md text-[0.55rem] font-bold transition-all"
                style={(() => {
                  if (i === idx) return { background: 'var(--brand)', color: '#fff' }
                  if (!state.feedbackShown[i]) return { background: 'var(--surface-2)', color: 'var(--text-3)' }
                  const q = questions[i]
                  const ans = state.answers[i] ?? (q.type === 'msq' ? [] : '')
                  const credit = q.type === 'short_answer' ? 0
                    : q.type === 'msq' ? scoreMsqCredit(ans, q.correct_answers, Object.keys(q.options ?? {}).length)
                    : (scoreObjective(q, ans) ? 1 : 0)
                  if (credit === 1) return { background: 'rgba(74,222,128,0.2)',  color: '#4ade80' }
                  if (credit > 0)   return { background: 'rgba(251,191,36,0.2)',  color: '#fbbf24' }
                  return                  { background: 'rgba(248,113,113,0.2)', color: '#f87171' }
                })()}
              >{i + 1}</button>
            ))}
          </div>
        </div>

        <div className="card p-4 text-xs space-y-1.5">
          <p style={{ color: 'var(--text-3)' }}>Current question</p>
          <p className="font-semibold" style={{ color: 'var(--text-1)' }}>Q{idx + 1} of {total}</p>
          {questions[idx]?.topic && <p style={{ color: 'var(--text-2)' }}>{questions[idx].topic}</p>}
        </div>

        {hasAnyAnswer && (
          <button onClick={() => dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING })}
            className="btn-primary w-full justify-center">
            Finish &amp; Score
          </button>
        )}
        <button onClick={() => setShowQuit(true)}
          className="w-full text-xs py-2 rounded-xl transition-all"
          style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.3)' }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.borderColor = 'var(--border)' }}
        >
          Quit exam
        </button>
      </aside>

      {/* ── Main content ─────────────────────────────────────── */}
      <div className="flex-1 min-w-0 xl:flex-none xl:w-[950px]">

        {/* Mobile progress bar */}
        <div className="lg:hidden h-1 rounded-full mb-4 overflow-hidden" style={{ background: 'var(--surface-2)' }}>
          <div className="h-full rounded-full transition-all duration-500"
               style={{ width: `${(answered / total) * 100}%`, background: 'var(--brand)' }} />
        </div>

        {/* Question card */}
        <div className="card p-4 sm:p-6 lg:p-8 overflow-y-auto overflow-x-hidden w-full"
             style={{ height: '75vh' }}>
          <QuestionCard key={`${idx}-${key}`} q={questions[idx]} idx={idx} />
        </div>

        {/* Nav row */}
        <div className="flex items-center gap-3 mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <button onClick={() => go(idx - 1)} disabled={idx === 0} className="btn-ghost text-sm">← Prev</button>

          <div className="flex-1 flex items-center justify-center gap-1 overflow-hidden">
            {total <= 20 ? questions.map((_, i) => (
              <button key={i} onClick={() => go(i)} className="rounded-full transition-all flex-shrink-0"
                style={{
                  width: i === idx ? '16px' : '8px', height: '8px',
                  background: i === idx ? 'var(--brand)' : state.feedbackShown[i] ? '#4ade8099' : 'var(--text-3)',
                }} />
            )) : (
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>{answered}/{total} answered</span>
            )}
          </div>

          {idx < total - 1 ? (
            <button onClick={() => go(idx + 1)} className="btn-ghost text-sm">Next →</button>
          ) : (
            <button onClick={() => dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING })}
              className="btn-primary text-sm">Finish &amp; Score</button>
          )}
        </div>

        {/* Mobile action buttons */}
        <div className="lg:hidden flex gap-3 mt-3">
          {hasAnyAnswer && (
            <button onClick={() => dispatch({ type: 'SET_STAGE', payload: EXAM_STAGES.GRADING })}
              className="btn-primary flex-1 justify-center text-sm">
              Finish &amp; Score
            </button>
          )}
          <button onClick={() => setShowQuit(true)}
            className="flex-1 text-xs py-2 rounded-xl transition-all"
            style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}>
            Quit exam
          </button>
        </div>

      </div>
    </div>
    </>
  )
}
